/**
 * Single-stream full resolution (design §7.3): full-resolution proxies are
 * made on demand for the file at the playhead and the next, and each file
 * plays from its normal proxy until its full one is ready.
 *
 * @module
 */
import { api, unwrap, type ProxyTiming } from "../api/client";
import type { ProxySource } from "./chain";
import { fileAt, type PlanFile } from "./plan";

export interface FullApi {
  /** none | queued | running | ready | failed */
  status(fileId: string): Promise<{ state: string; error?: string | null }>;
  request(fileId: string): Promise<void>;
  timing(fileId: string): Promise<ProxyTiming>;
  url(fileId: string): string;
}

export function apiFull(): FullApi {
  const path = (id: string) => ({ params: { path: { id } } });
  return {
    status: (id) => unwrap(api.GET("/api/files/{id}/proxy/full/status", path(id))),
    request: (id) => unwrap(api.POST("/api/files/{id}/proxy/full", path(id))).then(() => undefined),
    timing: (id) => unwrap(api.GET("/api/files/{id}/proxy/timing", path(id))),
    url: (id) => `/api/files/${id}/proxy/full`,
  };
}

export class FullResolution {
  /** Each ready file's video start in its full proxy (s). */
  private readonly ready = new Map<string, number>();
  private readonly requested = new Set<string>();
  private readonly failed = new Map<string, string>();
  private polling = false;

  constructor(private readonly full: FullApi) {}

  /** Files whose full proxy is ready. */
  get readyIds(): ReadonlySet<string> {
    return new Set(this.ready.keys());
  }

  isReady(fileId: string): boolean {
    return this.ready.has(fileId);
  }

  error(fileId: string): string | null {
    return this.failed.get(fileId) ?? null;
  }

  /** Requests and checks the full proxies of the file at (or next after) `utcMs` and the one after it. */
  async poll(files: readonly PlanFile[], utcMs: number): Promise<void> {
    if (this.polling) return;
    this.polling = true;
    try {
      const i = fileAt(files, utcMs);
      if (i < 0) return;
      for (const f of files.slice(i, i + 2)) {
        if (this.ready.has(f.id) || this.failed.has(f.id)) continue;
        const s = await this.full.status(f.id);
        if (s.state === "ready") {
          const t = await this.full.timing(f.id);
          this.ready.set(f.id, t.full_video_start_s ?? t.video_start_s ?? 0);
        } else if (s.state === "failed" && this.requested.has(f.id)) {
          this.failed.set(f.id, s.error ?? "failed");
        } else if (s.state === "none" || s.state === "failed") {
          this.requested.add(f.id);
          await this.full.request(f.id);
        }
      }
    } finally {
      this.polling = false;
    }
  }

  /** `base`, but with the full proxies of `ids` (ready ones only). */
  proxies(base: ProxySource, ids: ReadonlySet<string>): ProxySource {
    const startS = (id: string) => (ids.has(id) ? this.ready.get(id) : undefined);
    return {
      url: (id) => (startS(id) === undefined ? base.url(id) : this.full.url(id)),
      audioUrl: (id) => base.audioUrl(id),
      timing: (id) => {
        const s = startS(id);
        return s === undefined ? base.timing(id) : base.timing(id).then((t) => ({ ...t, video_start_s: s }));
      },
    };
  }
}
